// Typed commerce event taxonomy. The single source of truth for every
// PostHog event Märka emits — names AND property shapes. Feature code goes
// through `track(event, props)` rather than calling `posthog.capture` with
// a bare string + arbitrary object.
//
// Naming convention: `<noun>_<past-tense-verb>` (e.g. `cart_item_removed`).
// Stay short, lowercase, snake_case, and stable. Renaming an event is an
// analytics break — every existing dashboard, funnel, and saved insight
// keys off the literal name.
//
// PII rule: properties are metadata only. NEVER put user-typed text
// (configurator name, address, free-text fields) into a property. If the
// signal you need is "did the user type anything?", use a boolean
// (`has_text: true`) or a length bucket. The configurator's `name` is the
// canonical PII value and must never reach PostHog as a property.
//
// Where to capture: in event handlers (clicks, submits, form actions),
// not in effects. The only effect-driven capture we keep is the SDK's
// built-in pageview (task 132). Capturing inside `useEffect` causes
// double-fires under React StrictMode and triggers spurious events on
// re-renders.

import type { StickerConfiguration } from "@/domain/types";
import type { AppLocale } from "@/i18n/routing";

import posthog from "posthog-js";

type ProductKind = StickerConfiguration["kind"];

/**
 * Outcome of normalizing a Shopify `orders/paid` webhook payload — used as
 * a property on the server-side `order_paid` event so PostHog can split
 * funnel completion from broken/incomplete orders.
 */
export type NormalizationResult = "paid-order" | "failed";

/**
 * Why an add-to-cart / edit-to-cart submit failed.
 *
 * The four `snake_case` values mirror the `reason` a configurator server
 * action returns when it declines gracefully. The two `client_*` values have
 * no server counterpart:
 *   - `client_transport` — the server-action POST itself never came back
 *     (aborted by a navigation, dropped mobile connection, non-flight
 *     response). This is the dominant real-world failure mode.
 *   - `client_invalid_configuration` — the browser's own pre-submit check
 *     rejected the configuration, so no request was made. Should be
 *     unreachable (the CTA is disabled while invalid); if it shows up, the
 *     client and domain validators disagree.
 */
export type AddToCartFailureReason =
  | "invalid_configuration"
  | "cart_unavailable"
  | "line_unavailable"
  | "shopify_contract_error"
  | "ordering_paused"
  | "client_transport"
  | "client_invalid_configuration";

/** Which configurator server action a failure belongs to. */
export type AddToCartOp =
  | "submitConfiguratorAddToCart"
  | "submitConfiguratorEditToCart";

/**
 * Literal union of every event the app is allowed to capture. Adding an
 * event requires:
 *   1. Adding the literal here.
 *   2. Adding its property shape to `EventPropsMap` below.
 *   3. Wiring the call through `track()` (or `trackServer()`).
 */
export type MarkaEventName =
  | "sticker_added_to_cart"
  | "sticker_edit_saved"
  | "add_to_cart_failed"
  | "add_to_cart_blocked"
  | "add_to_cart_popup_shown"
  | "create_another_clicked"
  | "go_to_cart_clicked"
  | "cart_quantity_updated"
  | "cart_item_removed"
  | "checkout_initiated"
  | "phone_upsell_shown"
  | "phone_upsell_accepted"
  | "phone_upsell_declined"
  | "order_paid"
  | "thank_you_viewed"
  | "article_viewed"
  | "article_listing_viewed";

/** What the phone-sticker offer quoted, shared by its three events. */
export interface PhoneUpsellOfferProps {
  /** Ladder-eligible sheets already in the cart when the offer was made. */
  cart_sheet_count: number;
  /** The offered sheet's ladder price, in major units (kronor) like `cart_total`. */
  offer_price: number;
  /** What the ladder takes off that sheet, in major units. */
  offer_savings: number;
  currency: string;
}

/**
 * Per-event property shape. The `track()` helper enforces the matching
 * shape at compile time — passing the wrong props for an event name is a
 * type error.
 */
export interface EventPropsMap {
  sticker_added_to_cart: {
    sticker_kind: ProductKind;
    /** Granular product identity. See `getProductSlug`. */
    product_slug: string;
    quantity: number;
    cart_total_quantity: number;
  };
  sticker_edit_saved: {
    sticker_kind: ProductKind;
    /** Granular product identity. See `getProductSlug`. */
    product_slug: string;
    quantity: number;
    cart_total_quantity: number;
  };
  add_to_cart_failed: {
    op: AddToCartOp;
    reason: AddToCartFailureReason;
    /** True when the automatic single retry also failed. */
    retried: boolean;
    /**
     * Which recovery step ended a `client_transport` submission: the cart
     * probe or the retry. Absent for server-declined and validation
     * failures, where no recovery ladder runs.
     */
    stage?: "probe" | "retry";
    // Product dimensions are absent for `client_invalid_configuration`,
    // where the configuration is by definition incomplete.
    sticker_kind?: ProductKind;
    /** Granular product identity. See `getProductSlug`. */
    product_slug?: string;
    quantity?: number;
  };
  /**
   * The buyer asked to add to cart and client-side validation said no, so no
   * request was made. Distinct from `add_to_cart_failed` — nothing failed, the
   * configuration is not ready. Captured because the CTA used to be natively
   * `disabled` in this state, which swallows the click event: these attempts
   * were invisible to analytics and showed up only in session replay.
   */
  add_to_cart_blocked: {
    op: AddToCartOp;
    /**
     * The first validation code standing in the way (e.g. `line1.too_long`).
     * Codes only — never the typed value, which is `data-ph-no-capture` in the
     * configurator and must not re-enter via an event payload.
     */
    reason: string;
    sticker_kind: ProductKind;
  };
  /**
   * The popup that confirms a fresh add and offers "Skapa en till" / "Gå till
   * varukorgen" (spec #107, #112). Captured in the submit success branch that
   * opens it, never on edits.
   */
  add_to_cart_popup_shown: {
    /** Ladder-eligible sheets (Standard + Mix) in the cart after the add. */
    cart_sheet_count: number;
    /**
     * What the next sheet of the type just added would cost, in kronor (the
     * same decimal unit as `checkout_initiated.cart_total`), as quoted by the
     * sheet ladder and shown in the popup hint. Absent when no price was
     * known at submit time, which leaves the popup without a hint.
     */
    next_sheet_price?: number;
  };
  /** "Skapa en till" in the add-to-cart popup. */
  create_another_clicked: Record<string, never>;
  /** "Gå till varukorgen" in the add-to-cart popup. Sent instantly: a navigation follows. */
  go_to_cart_clicked: Record<string, never>;
  cart_quantity_updated: {
    line_id: string;
    new_quantity: number;
  };
  cart_item_removed: {
    line_id: string;
  };
  checkout_initiated: {
    cart_total: number;
    currency: string;
    line_count: number;
  };
  /**
   * The phone-sticker offer opened on the way to the cart (spec #107).
   * Metadata only: the number the buyer types never enters any phone-upsell
   * event.
   */
  phone_upsell_shown: PhoneUpsellOfferProps;
  /** The buyer accepted and the sheet landed in the cart; the cart follows. */
  phone_upsell_accepted: PhoneUpsellOfferProps;
  phone_upsell_declined: PhoneUpsellOfferProps & {
    /**
     * `button`: the decline action, which continues to the cart.
     * `dismissed`: closed the popup (backdrop, Escape, ×) and stayed on the page.
     */
    method: "button" | "dismissed";
  };
  order_paid: {
    order_id: string;
    normalization_result: NormalizationResult;
    total?: number;
    currency?: string;
    line_count?: number;
    product_kinds?: ProductKind[];
    /** Distinct product slugs across the order's line items. See `getProductSlug`. */
    product_slugs?: string[];
    /**
     * Name-sticker sheets that count toward the sheet ladder (Standard + Mix,
     * DEC-0036) — not the order's total quantity.
     */
    sheet_count?: number;
    /**
     * The ladder discount Shopify charged: automatic-discount allocations on
     * those sheets, in the order currency's major unit (kronor), like `total`.
     */
    ladder_discount?: number;
  };
  thank_you_viewed: {
    locale: AppLocale;
    checkout_return: boolean;
  };
  article_viewed: {
    slug: string;
    locale: AppLocale;
    tags: string[];
  };
  article_listing_viewed: {
    tag?: string;
    locale: AppLocale;
  };
}

/**
 * Stable, dashboard-friendly product identifier derived from a sticker
 * configuration. Used as the `product_slug` event property so PostHog can
 * group / filter by exact product (e.g. "Single - Small" vs
 * "Single - Large") without needing a per-event size column.
 *
 * Today: `single-{sizeId}` for singles, `mix` for the single mix variant.
 * Future: extend to `mix-{variantSlug}` when multiple mix products ship —
 * the format must stay stable, so prefer a domain-owned slug over a raw
 * Shopify GID.
 */
export function getProductSlug(config: StickerConfiguration): string {
  if (config.kind === "single") {
    return `single-${config.size}`;
  }
  return "mix";
}

/**
 * Optional per-call capture controls. The only flag the app currently uses
 * is `send_instantly`, which forces an immediate flush — required when the
 * page is about to navigate (e.g. clicking a checkout link) so the request
 * is on the wire before the browser tears the document down.
 */
export interface TrackOptions {
  send_instantly?: boolean;
}

/**
 * Capture a typed event from the browser. No-ops when the SDK hasn't yet
 * initialized (e.g. token missing, before consent opt-in, SSR pass), so
 * callers can wire it unconditionally.
 *
 * Synchronous and side-effect-free apart from the PostHog call. If a
 * caller needs debouncing (e.g. a `configuration_changed` driven by a
 * text input), debounce at the call site, not here.
 */
export function track<E extends MarkaEventName>(
  event: E,
  properties: EventPropsMap[E],
  options?: TrackOptions,
): void {
  if (typeof window === "undefined") {
    return;
  }
  // `__loaded` flips true after `posthog.init(...)` finishes; until then,
  // `capture` would queue silently but we'd rather skip than risk drops.
  if (!(posthog as unknown as { __loaded?: boolean }).__loaded) {
    return;
  }
  posthog.capture(event, properties, options);
}
